import axios from 'axios';
import { FixcenterProduct } from '../../../shared/models/fixcenter-product.model';
import { IShopingCartProduct } from '../../order/interfaces/shopping-cart-product.interface';
import { IShopingCart } from '../../order/interfaces/shopping-cart.interface';
import { ProductPricing } from '../../../shared/models/product-pricing.model';
import { Utils } from '../../../shared/constants/utilities.constants';
import { IBuildingSiteDto } from '../../order/interfaces/building-site.interface';
import { IBuildingSiteListViewDto } from '../../order/interfaces/building-site-list-view.interface';
import { CartOrderFormViewModel } from '../../../containers/shopping-cart-container/cart-order-form/cart-order-form-view.model';
import { IShopingCartDetail } from '../../order/interfaces/shopping-cart-detail.interface';
import { IBuildingSiteAddressListView } from '../../order/interfaces/building-site-address-list-view.interface';
import { IBuyerContactListView } from '../../order/interfaces/buyer-contact-list-view.interface';
import { IProductPackagingDto } from '../../../shared/models/product-packaging-dto.model';
import { IAssignContractorContactDto } from '../../order/interfaces/assign-contractor-contact-dto.interface';
import { IAddContractorContactDto } from '../../order/interfaces/add-contractor-contact-dto.interface';

const prefix: string = 'ShoppingCart/';
const roundFactor: number = 2;
const netPriceRoundFactor: number = 4;
export const shopingCartAlertVisibilityTimeInMs: number = 10000;
export const shopingCartAlertMaxWidth: number = 500;

export const getShoppingCart = () => {
    const languageCode = localStorage.getItem('lang');
    return axios.get(`${prefix}GetShoppingCart?languageCode=${languageCode}`);
};

export const getBuildingSites = (dto: IBuildingSiteDto) => {
    return axios.post(`${prefix}GetBuildingSites`, dto);
};

export const getBuildingSitesAddresses = (contractorUuid: string, page: number) => {
    return axios.get(`${prefix}GetBuildingSitesAddresses?contractorUuid=${contractorUuid}&page=${page}`);
};

export const getBuyerContacts = (contractorUuid: string, page: number) => {
    return axios.get(`${prefix}GetBuyerContacts?contractorUuid=${contractorUuid}&page=${page}`);
};

export const addToShoppingCart = (item: IShopingCartProduct) => {
    const languageCode = localStorage.getItem('lang');
    return axios.put(`${prefix}AddToShoppingCart?languageCode=${languageCode}`, item);
};

export const removeProductById = (productId: string) => {
    const languageCode = localStorage.getItem('lang');
    return axios.delete(`${prefix}RemoveProductById/${productId}?languageCode=${languageCode}`);
};

export const removeAll = () => {
    const languageCode = localStorage.getItem('lang');
    return axios.delete(`${prefix}RemoveAll?languageCode=${languageCode}`);
};

export const updateQuantity = (productId: string, newQuantity: number) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}UpdateQuantity/${productId}/${newQuantity}?languageCode=${languageCode}`);
};

// Sets the building site, clears the (now stale) delivery address/contact and refreshes prices/shipping,
// all in one save - use this for a user-driven building-site change instead of the three separate
// updateOrAdd*/updateShoppingCartAfterChangeBuildingSite calls, which race each other.
export const changeBuildingSite = (buildingSite: IBuildingSiteListViewDto) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}ChangeBuildingSite?languageCode=${languageCode}`, buildingSite);
};

export const syncBuildingSiteFromRecentSelection = (buildingSite: IBuildingSiteListViewDto) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}SyncBuildingSiteFromRecentSelection?languageCode=${languageCode}`, buildingSite);
};

export const updateOrAddBuildingSiteAddress = (buildingSiteAddress: IBuildingSiteAddressListView) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}UpdateOrAddBuildingSiteAddress?languageCode=${languageCode}`, buildingSiteAddress);
};

export const updateOrAddBuyerContact = (buyerContact: IBuyerContactListView) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}UpdateOrAddBuyerContact?languageCode=${languageCode}`, buyerContact);
};

export const assignContractorContact = (dto: IAssignContractorContactDto) => {
    return axios.post(`${prefix}AssignContractorContact`, dto);
};

export const addContractorContact = (dto: IAddContractorContactDto) => {
    return axios.post(`${prefix}AddContractorContact`, dto);
};

export const getContactTypes = () => {
    return axios.get(`${prefix}GetContactTypes`);
};

export const updateShoppingCartDetails = (detail: IShopingCartDetail) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}UpdateShoppingCartDetails?languageCode=${languageCode}`, detail);
};

export const reOpenOrderInShoppingCart = (documentId: string) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}ReOpenOrderInShoppingCart/${documentId}/${languageCode}`);
};

export const getExistOrderAsShoppingCart = (documentId: string) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}GetExistOrderAsShoppingCart/${documentId}/${languageCode}`);
};

export const getPdfOrderSummary = (
    documentId: string,
    documentNumber: string,
    documentDate: Date,
    documentStatusName: string
) => {
    const languageCode = localStorage.getItem('lang');
    return axios.get(
        `${prefix}GetPdfOrderSummary?documentId=${documentId}&languageCode=${languageCode}&documentNumber=${documentNumber}&documentDate=${documentDate}&documentStatusName=${documentStatusName}`
    );
};

export const getExcelOrderSummary = (
    documentId: string,
    documentNumber: string,
    documentDate: Date,
    documentStatusName: string
) => {
    const languageCode = localStorage.getItem('lang');
    return axios.get(
        `${prefix}GetExcelOrderSummary?documentId=${documentId}&languageCode=${languageCode}&documentNumber=${documentNumber}&documentDate=${documentDate}&documentStatusName=${documentStatusName}`
    );
};

export function uploadBondFile(dto: FormData) {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}UploadBondFile?languageCode=${languageCode}`, dto);
}

export const getTotalQuantityShoppingCart = (shopingCart: IShopingCart) => {
    return shopingCart.products.reduce((a, v) => (a = a + v.quantity), 0);
};

export const getShopingCartProduct = (
    product: FixcenterProduct,
    quantity: number,
    minQuantity: number,
    netPrice: number,
    brutPrice: number,
    discount: number,
    familyCode: string,
    imageFileId: string | null,
    productParentUuid?: string
) => {
    const salePackagingUnit: IProductPackagingDto =
        Array.isArray(product.salePackagingUnits) && product.salePackagingUnits.length > 0
            ? product.salePackagingUnits[0]
            : ({} as IProductPackagingDto);
    return {
        minQuantity: minQuantity,
        orderQuantity:
            !Utils.isNullOrUndefined(salePackagingUnit.orderQty) && salePackagingUnit.orderQty > 0
                ? salePackagingUnit.orderQty
                : 1,
        productChildUuid: product.id,
        productName: product.translation ? product.translation : product.name,
        quantity: quantity,
        productParentUuid: productParentUuid,
        articleCode: product.code,
        saleCurrencyName: product.saleCurrencyName,
        discount: discount,
        familyCode: familyCode,
        imageFileId: imageFileId,
        netto: netPrice,
        brutto: brutPrice,
        salesUnitId: !Utils.isNullOrUndefined(salePackagingUnit.unit) ? salePackagingUnit.unit : null,
    } as IShopingCartProduct;
};

export const getPriceWithPrecision = (value: number) => {
    return Utils.getNumberAsStringWithPrecision(value, roundFactor);
};

export const getNetPriceWithPrecision = (value: number) => {
    return Utils.getNumberAsStringWithPrecision(value, netPriceRoundFactor);
};

export const calculateTotalPricePerProduct = (price: number, quantity: number, isDisabled: boolean) => {
    if (isDisabled) {
        return 0;
    }
    let totalPrice: number = 0;
    if (price) {
        const value: number = price;
        totalPrice = value * quantity;
    }
    return Utils.getNumberAsStringWithPrecision(totalPrice, roundFactor);
};

export const mapAndGetInitialFormValues = (detail: IShopingCartDetail) => {
    if (detail) {
        return {
            reference: detail.reference,
            comments: detail.comments,
        } as CartOrderFormViewModel;
    }
    return new CartOrderFormViewModel();
};
